import { ChangeEvent, useEffect } from 'react';
import {
  AutomatedBreaksWaivingBreakMode,
  AutomatedBreaksWaivingMode,
} from '@sdk/authentication/types/apiTypes';
import { AutomatedBreakType } from '@sdk/settings/types';

import { TabType } from '@web/settings/types';
import { useSettingsErrorsContext } from '@web/settings/SettingsErrorContext';
import { AutomatedBreakFormFields } from '@web/settings/components/schedule/Breaks/breaks.types';
import { getSafeNumber } from '@web/settings/components/schedule/Breaks/AutomatedBreak/automatedBreak.utils';
import { useAutomatedBreaksSettings } from '@web/settings/components/schedule/Breaks/breaks.hooks';

type Props = {
  fields: AutomatedBreakFormFields;
  automatedBreakType: AutomatedBreakType;
};

export const useWaivingSettings = ({ fields, automatedBreakType }: Props) => {
  const { updateAutomatedBreaksSettings, automatedBreaks } = useAutomatedBreaksSettings();
  const { toggleError } = useSettingsErrorsContext();

  useEffect(
    () => {
      if (automatedBreakType !== AutomatedBreakType.meal) {
        return;
      }

      toggleError(
        TabType.schedule,
        'automatedBreaksMealWaivingFirstBreakIfLessThanMinutes',
        !!fields.waivingFirstBreakLessThanHours.runValidation()
      );
    },
    automatedBreakType === AutomatedBreakType.meal
      ? [
          fields.waivingMode.value,
          fields.waivingMode.defaultValue,
          fields.breakThreshold.value,
          fields.breakThreshold.defaultValue,
          fields.waivingFirstBreakLessThanHours.value,
          fields.waivingFirstBreakMode.value,
          fields.waivingFirstBreakLessThanHours.defaultValue,
          fields.waivingFirstBreakMode.defaultValue,
        ]
      : []
  );

  useEffect(
    () => {
      if (automatedBreakType !== AutomatedBreakType.meal) {
        return;
      }

      toggleError(
        TabType.schedule,
        'automatedBreaksMealWaivingSecondBreakIfLessThanMinutes',
        !!fields.waivingSecondBreakLessThanHours.runValidation()
      );
    },
    automatedBreakType === AutomatedBreakType.meal
      ? [
          fields.waivingMode.value,
          fields.waivingMode.defaultValue,
          fields.breakThreshold.value,
          fields.breakThreshold.defaultValue,
          fields.waivingSecondBreakLessThanHours.value,
          fields.waivingSecondBreakMode.value,
          fields.waivingSecondBreakLessThanHours.defaultValue,
          fields.waivingSecondBreakMode.defaultValue,
        ]
      : []
  );

  const updateWaivingEnabled = (isWaivingEnabled: boolean) => {
    fields.isWaivingEnabled.onChange(isWaivingEnabled);
    updateAutomatedBreaksSettings({
      [automatedBreakType === AutomatedBreakType.meal
        ? 'automatedBreaksMealWaivingIsEnabled'
        : 'automatedBreaksRestWaivingIsEnabled']: isWaivingEnabled,
    });
  };

  const updateWaivingMode = (waivingMode: AutomatedBreaksWaivingMode) => {
    fields.waivingMode.onChange(waivingMode);
    updateAutomatedBreaksSettings({
      automatedBreaksMealWaivingMode: waivingMode,
    });
  };

  const updateWaivingFirstBreakMode = (waivingFirstBreakMode: AutomatedBreaksWaivingBreakMode) => {
    fields.waivingFirstBreakMode.onChange(waivingFirstBreakMode);

    const updateObj: {
      automatedBreaksMealWaivingFirstBreakMode: AutomatedBreaksWaivingBreakMode;
      automatedBreaksMealWaivingFirstBreakIfLessThanMinutes?: number;
    } = {
      automatedBreaksMealWaivingFirstBreakMode: waivingFirstBreakMode,
    };

    if (
      waivingFirstBreakMode === AutomatedBreaksWaivingBreakMode.IF_EMPLOYEE_WORKS_LESS_THAN_HOURS &&
      automatedBreaks.meal.waivingFirstBreakLessThanHours <= 0
    ) {
      updateObj.automatedBreaksMealWaivingFirstBreakIfLessThanMinutes =
        automatedBreaks.meal.breakThreshold;

      fields.waivingFirstBreakLessThanHours.onChange(
        String(automatedBreaks.meal.breakThreshold / 60)
      );
    }

    updateAutomatedBreaksSettings(updateObj);
  };

  const updateWaivingSecondBreakMode = (
    waivingSecondBreakMode: AutomatedBreaksWaivingBreakMode
  ) => {
    fields.waivingSecondBreakMode.onChange(waivingSecondBreakMode);

    const updateObj: {
      automatedBreaksMealWaivingSecondBreakMode: AutomatedBreaksWaivingBreakMode;
      automatedBreaksMealWaivingSecondBreakIfLessThanMinutes?: number;
    } = {
      automatedBreaksMealWaivingSecondBreakMode: waivingSecondBreakMode,
    };

    if (
      waivingSecondBreakMode ===
        AutomatedBreaksWaivingBreakMode.IF_EMPLOYEE_WORKS_LESS_THAN_HOURS &&
      automatedBreaks.meal.waivingSecondBreakLessThanHours <= 0
    ) {
      updateObj.automatedBreaksMealWaivingSecondBreakIfLessThanMinutes =
        automatedBreaks.meal.breakThreshold * 2; // it's for the second break so the default cutoff is twice the break threshold

      fields.waivingSecondBreakLessThanHours.onChange(
        String((automatedBreaks.meal.breakThreshold * 2) / 60)
      );
    }

    updateAutomatedBreaksSettings(updateObj);
  };

  const updateWaivingFirstBreakLessThanHours = (event: ChangeEvent<HTMLInputElement>) => {
    const waivingFirstBreakLessThanHours = event.currentTarget.value;
    fields.waivingFirstBreakLessThanHours.onChange(waivingFirstBreakLessThanHours);
    updateAutomatedBreaksSettings({
      automatedBreaksMealWaivingFirstBreakIfLessThanMinutes:
        60 * getSafeNumber(waivingFirstBreakLessThanHours),
    });
  };

  const updateWaivingSecondBreakLessThanHours = (event: ChangeEvent<HTMLInputElement>) => {
    const waivingSecondBreakLessThanHours = event.currentTarget.value;
    fields.waivingSecondBreakLessThanHours.onChange(waivingSecondBreakLessThanHours);
    updateAutomatedBreaksSettings({
      automatedBreaksMealWaivingSecondBreakIfLessThanMinutes:
        60 * getSafeNumber(waivingSecondBreakLessThanHours),
    });
  };

  return {
    updateWaivingEnabled,
    updateWaivingMode,
    updateWaivingFirstBreakMode,
    updateWaivingSecondBreakMode,
    updateWaivingFirstBreakLessThanHours,
    updateWaivingSecondBreakLessThanHours,
  };
};
